import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/react';
import { Sparkles, Check } from 'lucide-react';

const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: 200px 0; }
`;

const Wrapper = styled.div`
  margin: 12px 0;
`;

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 10px;
  background: ${p => p.$done
    ? 'rgba(76, 175, 80, 0.08)'
    : 'rgba(124, 58, 237, 0.08)'};
  border: 1px solid ${p => p.$done
    ? 'rgba(76, 175, 80, 0.2)'
    : 'rgba(124, 58, 237, 0.2)'};
  font-size: 0.95rem;
  color: ${p => p.$done
    ? 'rgba(76, 175, 80, 0.95)'
    : 'rgba(192, 132, 252, 0.95)'};
  font-weight: 500;

  .shimmer-bar {
    flex: 1;
    height: 6px;
    border-radius: 3px;
    background: linear-gradient(90deg,
      rgba(124, 58, 237, 0.1) 0%,
      rgba(124, 58, 237, 0.35) 50%,
      rgba(124, 58, 237, 0.1) 100%);
    ${css`background-size: 200px 100%; animation: ${shimmer} 1.5s infinite;`}
  }
`;

const TimeEstimate = styled.div`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.3);
  margin-top: 6px;
  text-align: center;
`;

/**
 * AIProgressBar — status messages with shimmer animation.
 *
 * @param {string[]} messages - Status messages
 * @param {string} doneMessage - Message shown when done
 * @param {boolean} done - Whether the operation is complete
 * @param {number} interval - Time per message in ms (default 3000)
 * @param {boolean} sequential - If true, play through once and stay on last message (no loop)
 * @param {string} estimatedTime - Optional time estimate shown below the bar (e.g. "~30 sekuntia")
 */
export default function AIProgressBar({
  messages, doneMessage, done = false, interval = 3000,
  sequential = false, estimatedTime,
}) {
  const [msgIndex, setMsgIndex] = useState(0);
  const startTime = useRef(Date.now());

  useEffect(() => {
    if (done || !messages?.length) return;
    const timer = setInterval(() => {
      setMsgIndex(prev => {
        const next = prev + 1;
        if (sequential && next >= messages.length) {
          // Stay on last message
          return messages.length - 1;
        }
        return next % messages.length;
      });
    }, interval);
    return () => clearInterval(timer);
  }, [done, messages, interval, sequential]);

  // Reset index when messages change
  useEffect(() => {
    setMsgIndex(0);
    startTime.current = Date.now();
  }, [messages]);

  if (done) {
    return (
      <Wrapper>
        <Bar $done>
          <Check size={16} />
          {doneMessage || 'Valmis!'}
        </Bar>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Bar>
        <Sparkles size={16} />
        <span>{messages?.[msgIndex] || 'AI työskentelee...'}</span>
        <div className="shimmer-bar" />
      </Bar>
      {estimatedTime && (
        <TimeEstimate>{estimatedTime}</TimeEstimate>
      )}
    </Wrapper>
  );
}
